/**
 * The decision block's panel: Source, Question, Escalations.
 *
 * The three tabs are the three parts of a `decision` step. Source is
 * `triggers` — what makes Radar look again. Question is `question` plus the
 * `exits`, one per answer. Escalations is `escalations`, the nudges Radar sends
 * while it waits.
 *
 * Exits are named, not targeted, here: a target is the block the branch
 * continues at, and that is set by dragging on the canvas, where you can see
 * what you are pointing at.
 */

import { Checkbox } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import React, { FC, useState } from 'react';
import {
	AOPDecisionStep,
	AOPDraftStep,
	AOPSuggestion,
	AOPViolation,
	AOPTrigger,
} from '../../../types/AOPTypes';
import { OWSubTabs } from '../../ow_sub_tabs/OWSubTabs';
import { AOPSuggestionNote, AOPViolationNote } from '../AOPNotes';
import { AOPDecisionEscalations } from './AOPDecisionEscalations';
import { AOPDecisionQuestion } from './AOPDecisionQuestion';
import { AOP_TRIGGERS } from '../utils/AOPPlanUtils';
import { AOPPanelField } from './AOPPanelShell';

interface AOPDecisionPanelProps {
	step: AOPDecisionStep;
	/** Every step in the plan, in order — what a branch can be pointed at. */
	steps: AOPDraftStep[];
	/** What the service said about this step on the last draft read or write. */
	suggestions: AOPSuggestion[];
	violations: AOPViolation[];
	onAcceptSuggestion: (suggestionId: string) => void;
	isReadOnly: boolean;
	onChange: (patch: Partial<AOPDecisionStep>) => void;
}

export const AOPDecisionPanel: FC<AOPDecisionPanelProps> = ({
	step,
	steps,
	suggestions,
	violations,
	onAcceptSuggestion,
	isReadOnly,
	onChange,
}) => {
	// The question is what a decision step *is* — where it branches and what it
	// is waiting to hear. Source is where it came from, which matters once and
	// then never again, so opening a block on it buried the thing you clicked
	// for behind a tab.
	const [activeTab, setActiveTab] = useState('question');

	/*
	  Reminders are what Escalation mode is, so the tab is shut while the step
	  has none: a decision gives up either by waiting once or by nudging until it
	  runs out, and that is chosen on the Question tab. Opening an empty tab let
	  the mode be changed from two places, which is what most of this panel's
	  review was about — adding the first reminder here renamed the branch behind
	  the author's back, or failed to and made the draft unsaveable.
	*/
	const hasEscalations = (step.escalations ? step.escalations : []).length > 0;

	// Optional upstream, so absent and empty read the same — and every box can be
	// unticked, which the service takes today.
	const triggers = step.triggers ? step.triggers : [];

	const sourceTab = (
		<AOPPanelField label="Information used to determine the outcome to this decision" required>
			<Checkbox.Group
				className="flex flex-col gap-xs"
				value={triggers}
				disabled={isReadOnly}
				onChange={(next) => onChange({ triggers: next as AOPTrigger[] })}
				options={AOP_TRIGGERS.map((trigger) => ({
					label: trigger.description,
					value: trigger.value,
				}))}
			/>

			{/*
			  Said here rather than enforced by refusing the click: clearing the last
			  box is how an author changes their mind about which one they want, and
			  a checkbox that will not untick looks broken. The complaint stands
			  until one is back.

			  What it costs is not nothing — with no trigger Radar looks once on
			  entry and then only waits out the timeout, so the decision stops being
			  a decision and becomes a delay.
			*/}
			{triggers.length === 0 ? (
				<span className="bodyregular text-character-danger">
					Pick at least one. With none, Radar checks once when it reaches this step and then only
					waits out the timeout.
				</span>
			) : null}
		</AOPPanelField>
	);

	// Everything the service flags about a decision — an exit pointing nowhere, a
	// catch-all named for the wrong branch — is settled on the Question tab, so
	// that is where it is shown. Suggestions count too: an open one blocks a
	// publish just as a violation does.
	const openOnes = suggestions.filter((suggestion) => suggestion.status === 'open');
	const outstanding = violations.length + openOnes.length;

	return (
		<>
			<OWSubTabs
				tabs={[
					{ key: 'source', label: 'Source' },
					{
						key: 'question',
						label: (
							<span className="inline-flex items-center gap-2xs">
								Question
								{outstanding > 0 ? (
									<ExclamationCircleFilled className="text-character-danger" />
								) : null}
							</span>
						),
					},
					{ key: 'escalations', label: 'Escalations', disabled: !hasEscalations },
				]}
				activeKey={activeTab}
				onChange={setActiveTab}
			/>

			{/*
			  Shown on the Question tab, where everything the service flags about a
			  decision is settled: a violation has to be fixed, a suggestion ruled
			  on, and either blocks a publish.
			*/}
			{activeTab === 'question' && outstanding > 0 ? (
				<div className="flex flex-col gap-xs">
					{violations.map((violation, index) => (
						<AOPViolationNote key={index} violation={violation} />
					))}
					{openOnes.map((suggestion) => (
						<AOPSuggestionNote
							key={suggestion.id}
							suggestion={suggestion}
							onAccept={isReadOnly ? undefined : () => onAcceptSuggestion(suggestion.id)}
						/>
					))}
				</div>
			) : null}

			{activeTab === 'source' ? sourceTab : null}
			{activeTab === 'question' ? (
				<AOPDecisionQuestion
					step={step}
					steps={steps}
					isReadOnly={isReadOnly}
					onChange={onChange}
					onShowEscalations={() => setActiveTab('escalations')}
				/>
			) : null}
			{activeTab === 'escalations' && hasEscalations ? (
				<AOPDecisionEscalations
					step={step}
					isReadOnly={isReadOnly}
					onChange={onChange}
					// The tab shuts behind them, so they are put back on the question
					// rather than left looking at a strip with nothing selected.
					onEmptied={() => setActiveTab('question')}
				/>
			) : null}
		</>
	);
};
